import { type AnchorHTMLAttributes, type MouseEvent, useCallback, useSyncExternalStore } from 'react';

/** A deliberately tiny history router: six screens and a query string do not need a routing library. */

export type Route =
  | { screen: 'market' }
  | { screen: 'genres' }
  | { screen: 'island'; code: string | null }
  | { screen: 'concepts' }
  | { screen: 'mine' }
  | { screen: 'guide' }
  | { screen: 'settings'; tab: 'models' | 'account' }
  | { screen: 'login' };

export type ScreenKey = Route['screen'];

const NAV_EVENT = 'studio:navigate';

export function parseRoute(pathname: string): Route {
  const p = pathname.replace(/\/+$/, '') || '/';
  if (p === '/' || p === '/market') return { screen: 'market' };
  if (p === '/genres') return { screen: 'genres' };
  if (p === '/concepts') return { screen: 'concepts' };
  if (p === '/my-islands') return { screen: 'mine' };
  if (p === '/guide') return { screen: 'guide' };
  if (p === '/settings') return { screen: 'settings', tab: 'models' };
  if (p === '/settings/account') return { screen: 'settings', tab: 'account' };
  if (p === '/login') return { screen: 'login' };
  if (p === '/islands') return { screen: 'island', code: null };
  const m = /^\/islands\/([^/]+)$/.exec(p);
  if (m?.[1]) return { screen: 'island', code: decodeURIComponent(m[1]) };
  return { screen: 'market' };
}

export function screenPath(screen: ScreenKey): string {
  switch (screen) {
    case 'market':
      return '/';
    case 'genres':
      return '/genres';
    case 'island':
      return '/islands';
    case 'concepts':
      return '/concepts';
    case 'mine':
      return '/my-islands';
    case 'guide':
      return '/guide';
    case 'settings':
      return '/settings';
    case 'login':
      return '/login';
  }
}

export const islandPath = (code: string) => `/islands/${encodeURIComponent(code)}`;

function subscribe(cb: () => void): () => void {
  window.addEventListener('popstate', cb);
  window.addEventListener(NAV_EVENT, cb);
  return () => {
    window.removeEventListener('popstate', cb);
    window.removeEventListener(NAV_EVENT, cb);
  };
}

const getLocation = () => window.location.pathname + window.location.search;

export function navigate(to: string, opts: { replace?: boolean } = {}): void {
  if (to === getLocation()) return;
  if (opts.replace) window.history.replaceState(null, '', to);
  else window.history.pushState(null, '', to);
  window.dispatchEvent(new Event(NAV_EVENT));
  if (!opts.replace) window.scrollTo?.(0, 0);
}

export function useLocation(): { pathname: string; search: URLSearchParams; route: Route } {
  const loc = useSyncExternalStore(subscribe, getLocation, () => '/');
  const q = loc.indexOf('?');
  const pathname = q === -1 ? loc : loc.slice(0, q);
  return { pathname, search: new URLSearchParams(q === -1 ? '' : loc.slice(q)), route: parseRoute(pathname) };
}

/** Query-string state for filters, so a reload or a shared link keeps the view. */
export function useSearchParam(name: string): [string | null, (v: string | null) => void] {
  const { pathname, search } = useLocation();
  const value = search.get(name);
  const set = useCallback(
    (v: string | null) => {
      const next = new URLSearchParams(window.location.search);
      if (v === null || v === '') next.delete(name);
      else next.set(name, v);
      const s = next.toString();
      navigate(`${pathname}${s ? `?${s}` : ''}`, { replace: true });
    },
    [name, pathname],
  );
  return [value, set];
}

export function Link({ to, onClick, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement> & { to: string }) {
  const handle = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e);
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    navigate(to);
  };
  return <a href={to} onClick={handle} {...rest} />;
}
